import React from "react";
import {Badge, Button, Form, Table} from "react-bootstrap";
import "../../CommonCss/TableToggle.css"
import "../../CommonCss/ExtraColor.css"
import "../../CommonCss/TableFilterMultiSelect.css"
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import {saveAs} from "file-saver";
import {TableColumnFormatter} from "../../CommonFunc/TableColumnFormatter";

interface CustomColumnMeta {
    hidden?: boolean
    displayName?: string
    filterComponent?: (props: { column: any }) => React.ReactNode
    tableName?: string
    type?: string
    options?: string[]
}

// Extend base @tanstack/react-table to include custom meta field
declare module '@tanstack/react-table' {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    interface ColumnMeta<TData extends unknown, TValue> extends CustomColumnMeta {}
}

export const TanStackTableView: React.FC<{
    data: any[];
    columns: ColumnDef<any, any>[];
    tableName: string;
}> = ({ data, columns, tableName }) => {
    // Initialise column visibility based on column metadata from Glue catalog
    const [columnVisibility, setColumnVisibility] = React.useState<Record<string, boolean>>(() => {
        const initialVisibility: Record<string, boolean> = {}
        columns.forEach(col => {
            const key = col.id as string
            initialVisibility[key] = !col.meta?.hidden
        })
        return initialVisibility
        }
    )
    const [globalFilter, setGlobalFilter] = React.useState('')
    const [pageSize, setPageSize] = React.useState(10)
    const [pageIndex, setPageIndex] = React.useState(0)

    const CustomToggleList = () => (
        <div className="table-responsive p-3 mt-2 mb-2 border border-1 border-dark border-opacity-75">
            <p><Badge bg="primary">Show</Badge>/<Badge bg="secondary">Hide</Badge> columns</p>
            <div className="btn-group btn-group-toggle" data-toggle="buttons">
                {columns.map(column => {
                    const id = column.id as string
                    const displayName = column.meta?.displayName as string || id
                    return (
                        <button
                            key={id}
                            type="button"
                            className={`btn btn-sm toggle-small-text ${columnVisibility[id] ? 'btn-outline-primary' : 'btn-outline-secondary'}`}
                            onClick={() => {
                                setColumnVisibility(prev => ({
                                    ...prev,
                                    [id]: !prev[id],
                                }))
                            }}
                            style={{ borderRadius: 0 }}
                        >
                            {displayName}
                        </button>
                    )
                })}
            </div>
        </div>
    )

    const table = useReactTable({
        data,
        columns,
        state: {
            columnVisibility,
            globalFilter,
            pagination: {
                pageIndex,
                pageSize
            },
        },
        onColumnVisibilityChange: setColumnVisibility,
        onGlobalFilterChange: setGlobalFilter,
        getSortedRowModel: getSortedRowModel(),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        enableSorting: true,
        manualPagination: false,
        onPaginationChange: (updater) => {
            const next = typeof updater === "function" ? updater({ pageIndex, pageSize }) : updater
            setPageIndex(next.pageIndex)
            setPageSize(next.pageSize)
        },
    })

    const paginationSize = [10, 15, 20, 50, 100]
    const wrapperClass = `table-responsive ${columns.length <= 20 ? '' : 'table-small-text'}`
    const noData = table.getRowModel().rows.length === 0

    const handleExportCSV = () => {
        const visibleCols = table.getVisibleFlatColumns().map(col => col.id)
        const csvRows = [visibleCols.join(',')]

        table.getFilteredRowModel().rows.forEach(row => {
            const values = row.getVisibleCells().map(cell => JSON.stringify(cell.getValue() ?? ''))
            csvRows.push(values.join(','))
        })

        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8' })

        const now = new Date()
        const timestamp = now.toISOString().split('T')[0]

        saveAs(blob, `${tableName}_filtered_${timestamp}.csv`)
    }

    return (
        <>
            <div className="mb-2 d-flex justify-content-between align-items-center">
                <Form.Control
                    type="text"
                    placeholder={`Filter ${data.length} search results`}
                    value={globalFilter}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    style={{maxWidth: '300px'}}/>
                <Button
                    className={"btn-success btn-sm float-end"}
                    variant={"success"}
                    size={"sm"}
                    onClick={handleExportCSV}
                >
                    Export filtered row
                </Button>
            </div>

            <CustomToggleList/>

            <div className={wrapperClass}>
                <Table bordered hover className={columns.length > 20 ? 'table-sm' : ''}>
                    <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <th key={header.id}
                                    style={{cursor: header.column.getCanSort() ? 'cursor-pointer' : ''}}
                                >
                                    <div className='d-flex justify-content-between align-items-center w-100'>
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        {header.column.getCanSort() && (
                                            <span className="align-self-end mt-1"
                                                  onClick={header.column.getToggleSortingHandler()}
                                                  style={{
                                                      position: 'absolute',
                                                      right: '8px',
                                                      cursor: 'pointer'
                                                  }}
                                            >
                                                {{
                                                    asc: <span style={{color: 'darkgreen'}}>▲</span>,
                                                    desc: <span style={{color: 'darkgreen'}}>▼</span>
                                                }[header.column.getIsSorted() as string] || '▼'}
                                            </span>
                                        )}
                                    </div>
                                    {/* Render filterComponent if defined */}
                                    {header.column.getCanFilter() && header.column.columnDef.meta?.filterComponent && (
                                        <div>
                                            {header.column.columnDef.meta.filterComponent({column: header.column})}
                                        </div>
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody>
                    {noData ? (
                        <tr>
                            <td colSpan={columns.length}>
                                <h6 className="text-primary m-2">No data found!</h6>
                            </td>
                        </tr>
                    ) : (
                        table.getRowModel().rows.map((row) => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map((cell, colIndex) => {
                                    const className = TableColumnFormatter.renderCssClasses(
                                        cell.getValue(),
                                        cell.row.original,
                                        cell.row.index,
                                        colIndex,
                                        cell.column.id,
                                        tableName,
                                        columns.length
                                    )
                                    return <td className={`p-0 ${className} py-1 px-2`} key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                })}
                            </tr>
                        ))
                    )}
                    </tbody>
                </Table>
            </div>

            <div className={"d-flex justify-content-between align-items-center mt-3"}>
                <div className={"d-flex align-items-center gap-2"}>
                    <Form.Select
                        size={"sm"}
                        style={{width: "auto"}}
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                        {paginationSize.map((size) => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </Form.Select>
                    <span>
                        {`Showing rows ${
                            table.getFilteredRowModel().rows.length === 0
                                ? 0
                                : pageIndex * pageSize + 1
                        } to ${
                            Math.min((pageIndex + 1) * pageSize, table.getFilteredRowModel().rows.length)
                        } of ${table.getFilteredRowModel().rows.length}`}
                    </span>
                </div>

                <div className={"d-flex align-items-center gap-2"}>
                    <Button
                        onClick={() => setPageIndex(0)}
                        disabled={table.getState().pagination.pageIndex === 0}
                    >«</Button>
                    <Button
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >‹</Button>
                    <span>Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}</span>
                    <Button
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >›</Button>
                    <Button
                        onClick={() => setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                    >»</Button>
                </div>
            </div>
        </>
    )
}